import { ActionType } from '@editor/src/js/constants/common-action-type';
import { IMenuEventParams } from '@src/slide/application/service/menu-manager/menu-event/menu-event.interface';
import { IPictureModel } from '@src/slide/domain/ui-model/element-modeles/Interface/picture-model.interface';
import { ADDON_PREFIX } from '@src/slide/domain/ui-model/element-modeles/property/addon/addon.interface';
import {
    TDWActionType,
    TDWModuleType,
    TDWOperationType,
    TDWVer9Type,
} from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import DownloadManager from '@src/slide/application/service/download-manager';
import AttachmentUploader from '@src/js/common/ark-extensions/dc-components/attachment-uploader';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import MediaUtils from '@editor/src/page/index/components/canvas/element-types/image-element/media-container/media-utils';
import { getWcjsVideoCurrentImage } from '@src/js/common/ark-extensions/dc-components/wcjs-video-player';
import { uploadImageBlob } from '@src/slide/infrastructure/module/dependent/image/image-toolkit/cgi-upload';
import { RegionType } from '@src/slide/domain/selection/constant/region';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { ISlideBaseAppServices } from '@src/slide/application/entry/toc/base/slide-base-app-services.interface';
import { IAddonHandler } from '@src/slide/application/service/addon/addon-handler.interface';
import { ENABLE_WCJS_VIDEO_PLAYER_RENDERER } from '@src/slide/infrastructure/module/dependent/config/renderer/switch';
import { IEditorAppServices } from '@src/slide/application/entry/toc/editor/base/base-editor-app-service.interface';

function getAddonManager() {
    return (AppServiceProvider.getInstance().getAppServices() as ISlideBaseAppServices)
        .viewableServiceLayer.addonManager;
}

async function getCopyPasteImageService() {
    return (AppServiceProvider.getInstance().getAppServices() as ISlideBaseAppServices)
        .editableServiceLayer?.copyPasteImageService?.promise();
}

function getOcrService() {
    return (AppServiceProvider.getInstance().getAppServices() as IEditorAppServices)
        .editableServiceLayer?.ocrService;
}

/**
 * 事件列表（主要逻辑从model文件迁移而来）
 */
const menuEventMap = {
    // 旋转
    [ActionType.ROTATE]: (model: IPictureModel) => {
        model.handleRotate();
    },
    // 下载原图
    [ActionType.DOWNLOAD_PICTURE]: (model: IPictureModel) => {
        DownloadManager.downloadImg(model.props.src as string);
    },
    // 下载文件
    [ActionType.DOWNLOAD_ATTACHMENT]: (model: IPictureModel) => {
        if (model.isAttachment() || model.isMedia()) {
            const objKey = model.video?.link || model.audio?.link || model.props.attachment?.objKey;

            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            AttachmentUploader.getInstance().download(objKey!);

            // 上报
            tdw({
                opername: TDWOperationType.DOC_WORD_EDITOR,
                module: TDWModuleType.LOCAL_FILES,
                action: TDWActionType.DOWNLOAD_CLICK,
            });
        }
    },
    // 图片替换
    [ActionType.REPLACE_PICTURE]: (model: IPictureModel, menuEventParams: IMenuEventParams) => {
        const { values } = menuEventParams;
        model.handleReplacePicture(values);
    },
    // 提取图片文字
    [ActionType.OCR_PICTURE]: (model: IPictureModel, menuEventParams: IMenuEventParams) => {
        const { values } = menuEventParams;
        getOcrService()?.promise()
            .then(ocrService => ocrService.showOcrRightBar(model, undefined, values));
    },
    // 图片格式
    [ActionType.INSERT_PICTURE]: (model: IPictureModel, menuEventParams: IMenuEventParams) => {
        const { values, extraOption } = menuEventParams;
        model.handleAlpha(values, extraOption);
    },
    // 图片校正
    [ActionType.REGULATE]: (model: IPictureModel, menuEventParams: IMenuEventParams) => {
        const { values, extraOption } = menuEventParams;
        model.getImageEffect?.().handleImageEffect(values, extraOption);
    },
    // 图片效果
    [ActionType.PICTURE_COLOR]: (model: IPictureModel, menuEventParams: IMenuEventParams) => {
        const { values, extraOption } = menuEventParams;
        model.getImageEffect?.().handleImageEffect(values, extraOption);
    },
    // 删除链接
    [ActionType.REMOVE_LINK]: (model: IPictureModel) => {
        model.getLink().removeLink();
    },
    // 编辑链接
    [ActionType.EDIT_LINK]: (model: IPictureModel, menuEventParams: IMenuEventParams) => {
        const { values } = menuEventParams;
        // @ts-ignore
        model.getLink().updateLink({ href: values });
    },
    // video 从文件替换封面
    [ActionType.FILE_COVER]: async () => {
        const module = await getCopyPasteImageService();
        if (!module) return;
        const { ImageEditManager } = module;
        const imageManager = ImageEditManager.getInstance();

        const input = document.getElementById('select_pic_input_local_video');
        input?.click();
        imageManager.imageReplaceAction = 1; // 代表是替换图片
        imageManager.imageReplacing = true;
        imageManager.clipDataWaiting = false;
    },
    // video 从当前框架替换封面
    [ActionType.VIDEO_COVER]: async (model: IPictureModel) => {
        if (!ENABLE_WCJS_VIDEO_PLAYER_RENDERER) {
            console.warn('can not support wcjs video player');
            return;
        }
        const imageBlob = await getWcjsVideoCurrentImage(`${RegionType.EDITOR}-${model.key}`).catch(() => undefined);
        if (!imageBlob) {
            console.warn('can not set video cover: no blob');
            return;
        }
        uploadImageBlob({ blob: imageBlob })
            .then(({ url: imageUrl }) => {
                const image = new Image();
                image.onload = () => {
                    model.handleReplacePicture([{ width: image.width, height: image.height, src: image.src, imageType: 'jpeg' }]);
                };
                image.src = imageUrl;
            })
            .catch(() => {
                // 本地编辑可忽略错误，无需触发错误弹窗
                console.error('upload file error');
            });
    },
    // video 重置封面
    [ActionType.RESET_COVER]: (model: IPictureModel) => {
        if (!model.video?.link) {
            return;
        }
        const videoLink = model.video.link;
        const imageUrl = MediaUtils.VideoImageMap.get(videoLink);
        const size = MediaUtils.VideoSizeMap.get(videoLink);
        // 封面存在, 直接替换
        if (imageUrl && size) {
            model.handleReplacePicture([{ width: size.width, height: size.height, src: imageUrl, imageType: 'jpeg' }]);
            return;
        }
        // 封面不存在，重新上传重试一次
        MediaUtils.setVideoImageAndSize(model.video?.link).then(() => {
            const imageUrl = MediaUtils.VideoImageMap.get(videoLink);
            const size = MediaUtils.VideoSizeMap.get(videoLink);
            if (imageUrl && size) {
                model.handleReplacePicture([{ width: size.width, height: size.height, src: imageUrl, imageType: 'jpeg' }]);
            }
        });
    },
};

/**
 * 事件响应匹配
 * 返回boolean值，表示此事件是否已在model独有事件匹配上，匹配上返回TRUE，结束匹配，未匹配上返回FALSE，外层继续匹配通用事件
 * @param model 当前uiModel
 * @param menuEventParams 菜单传参
 */
export function handleMenuEvent(model: IPictureModel, menuEventParams: IMenuEventParams): boolean {
    const { actionType } = menuEventParams;
    if (menuEventMap[actionType]) {
        // 精准查找事件
        menuEventMap[actionType](model, menuEventParams);
        return true;
    }
    // 裁剪或裁剪为纵横比
    if (actionType.startsWith(ActionType.TAILOR_PIC) && !menuEventParams?.extraOption?.isMultiGroup) {
        // 范围匹配事件（裁剪）
        model.getImageCrop?.().handleCrop?.(actionType);
        return true;
    }
    if (actionType.startsWith(ADDON_PREFIX)) {
        // 范围匹配事件（插件）
        (getAddonManager().addonHandler as IAddonHandler)
            .handleAddonSceneItemClick(actionType, { ver9: TDWVer9Type.DETAIL_PIC_RIGHT_CLK });
        return true;
    }
    // 在model独有的事件中没有匹配到，返回false，外层继续匹配通用事件
    return false;
}
